// hooks/useUploadMutation.ts

import { useMutation } from '@tanstack/react-query';

import {
  getFirebaseStorageUrlService,
  uploadFileToFirebaseStorageService,
} from '@/services/httpServices/FirebaseStorageServices';
import type {
  FirebaseStorageUrlResponse,
  GetFirebaseStorageUrlConfig,
} from '@/services/httpServices/FirebaseStorageServices/types';
import {
  getUploadBucketUrlService,
  uploadFileToS3Service,
} from '@/services/httpServices/UploadServices';
import type { PresignRequestServiceConfig, PresignUrlResponse } from '@/services/httpServices/UploadServices/types';
import type { WebAppEnvironment } from '@/types/api';

export function useUploadMutation() {
  return useMutation<PresignUrlResponse, Error, PresignRequestServiceConfig>({
    mutationFn: async config => getUploadBucketUrlService(config),
  });
}

export function useS3UploadMutation() {
  return useMutation<
    undefined,
    Error,
    {
      presignedUrl: string;
      file: File;
      onProgress: (progress: number) => void;
      signal: AbortSignal;
    }
  >({
    mutationFn: async ({ presignedUrl, file, onProgress, signal }) => {
      await uploadFileToS3Service(presignedUrl, file, onProgress, signal);
      return undefined;
    },
  });
}

/**
 * Get a Firebase Storage URL for uploading
 * Similar pattern to useUploadMutation for S3
 */
export function useFirebaseStorageUrlMutation() {
  return useMutation<FirebaseStorageUrlResponse, Error, GetFirebaseStorageUrlConfig>({
    mutationFn: async config => getFirebaseStorageUrlService(config),
  });
}

/**
 * Upload to Firebase Storage using a prepared URL
 * Similar pattern to useS3UploadMutation
 */
export function useFirebaseUploadMutation() {
  return useMutation<
    string, // Returns download URL on success
    Error,
    {
      uploadUrl: string;
      file: File;
      onProgress: (progress: number) => void;
      signal: AbortSignal;
      targetEnv: WebAppEnvironment;
    }
  >({
    mutationFn: async ({ uploadUrl, file, onProgress, signal, targetEnv }) => {
      return uploadFileToFirebaseStorageService(uploadUrl, file, onProgress, signal, targetEnv);
    },
  });
}
